/**
 * PreferenceFieldToggles - host-side SECTION-level toggles (Iter 9.40 Slice I.l).
 *
 * Model:
 *   - Host decides which preference SECTIONS appear on intake + preference card + insights.
 *   - Universal sections (Allergies, Accessibility) are toggled as a whole - options cannot
 *     be individually toggled by the host (they're platform-wide canonical).
 *   - Makeup-specific sections (Skin Profile, My Foundation, Tones & Finish, etc.) are also
 *     toggled as a whole. Options inside each section come from admin master preferences.
 *
 * Persists to db.host_settings.intake_visibility.{cat} = {section_key: bool}.
 * Backend: GET/PUT /api/host/settings/intake-visibility (Slice I.f).
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Switch } from "@/components/ui/switch";
import { Loader2, Save, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

// Universal sections - shown as locked-option sections (host toggles the section, options stay platform-canonical)
const UNIVERSAL_SECTIONS = new Set(["allergies", "accessibility", "sensitivities"]);

const sectionKey = (f) => {
  const raw = f.section || f.section_label || "General";
  return String(raw).toLowerCase().replace(/\s+/g, "_");
};

export default function PreferenceFieldToggles({ eventId, category, fields }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [visibility, setVisibility] = useState({});  // {section_key: bool}
  const cat = String(category || "").replace(/^cat_/, "");

  // Group fields by section_key
  const sections = useMemo(() => {
    const map = new Map();
    (fields || []).forEach((f) => {
      const k = sectionKey(f);
      if (!map.has(k)) {
        map.set(k, {
          key: k,
          label: f.section_label || f.section || "General",
          is_universal: UNIVERSAL_SECTIONS.has(k) || !!f.universal,
          fields: [],
        });
      }
      map.get(k).fields.push(f);
    });
    return Array.from(map.values());
  }, [fields]);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    axios
      .get(`${API}/host/settings/intake-visibility?category=${cat}`, { headers: auth() })
      .then((r) => {
        if (cancel) return;
        const v = (r.data && r.data.visibility) || {};
        // First-time load - default every section ON
        if (Object.keys(v).length === 0) {
          const seeded = {};
          sections.forEach((s) => { seeded[s.key] = true; });
          setVisibility(seeded);
        } else {
          setVisibility(v);
        }
      })
      .catch(() => {
        const seeded = {};
        sections.forEach((s) => { seeded[s.key] = true; });
        setVisibility(seeded);
      })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [eventId, cat, sections.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSection = (k) => setVisibility((prev) => ({ ...prev, [k]: prev[k] === false ? true : !prev[k] }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/host/settings/intake-visibility`,
        { category: cat, visibility },
        { headers: auth() }
      );
      toast.success("Saved · Insights, intake & preference cards will follow these toggles");
    } catch (e) {
      toast.error("Couldn't save: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
      </div>
    );
  }

  const enabledCount = sections.filter((s) => visibility[s.key] !== false).length;

  return (
    <div className="space-y-4" data-testid="pref-field-toggles">
      <div className="flex items-center justify-between text-xs text-stone-500">
        <span>{enabledCount} of {sections.length} sections enabled</span>
        <Button
          size="sm"
          onClick={save}
          disabled={saving}
          data-testid="pref-field-toggles-save"
          className="bg-purple-600 hover:bg-purple-700 text-white"
        >
          <Save className="w-3.5 h-3.5 mr-1" />
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>

      <div className="rounded-lg border border-stone-200 divide-y divide-stone-100">
        {sections.map((s) => {
          const checked = visibility[s.key] !== false;
          return (
            <div key={s.key} className="px-3 py-3" data-testid={`pref-section-row-${s.key}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-stone-900">{s.label}</p>
                    {s.is_universal && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-500 font-medium" data-testid={`universal-badge-${s.key}`}>
                        <Lock className="w-2.5 h-2.5" /> universal
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-stone-500 mt-0.5">
                    {s.is_universal
                      ? `Options are platform-canonical - you toggle the section, individual options stay locked.`
                      : `${s.fields.length} field${s.fields.length === 1 ? "" : "s"} in this section`}
                  </p>
                </div>
                <Switch
                  checked={checked}
                  onCheckedChange={() => toggleSection(s.key)}
                  data-testid={`pref-section-toggle-${s.key}`}
                />
              </div>
              {checked && !s.is_universal && (
                <div className="mt-2 flex flex-wrap gap-1.5 pl-1">
                  {s.fields.slice(0, 8).map((f) => (
                    <span key={f.key || f.label} className="text-[10px] px-2 py-0.5 rounded-full bg-stone-50 text-stone-600 border border-stone-200" data-testid={`pref-section-field-${f.key}`}>
                      {f.label}
                    </span>
                  ))}
                  {s.fields.length > 8 && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-stone-50 text-stone-400">+{s.fields.length - 8} more</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
